// src/app/services/data-transfer.service.ts
import { isPlatformServer } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable, makeStateKey, PLATFORM_ID, TransferState } from '@angular/core';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class DataTransferService {
  private baseUrl = environment.serverUrl;
  private http = inject(HttpClient);
  private transferState = inject(TransferState);
  private platformId = inject(PLATFORM_ID);
  
  // Check if we're running on server
  private isServer = isPlatformServer(this.platformId);

  // Method for GET requests with SSR support
  fetchData<T>(url: string, stateKey: string): Observable<T> {
    const fullUrl = `${this.baseUrl}` + url;
    const STATE_KEY = makeStateKey<T>(stateKey);
    
    // Client-side: Check if data is available in TransferState
    if (!this.isServer && this.transferState.hasKey(STATE_KEY)) {
      const data = this.transferState.get(STATE_KEY, null as T);
      this.transferState.remove(STATE_KEY); // Clean up
      return of(data);
    }
    
    // Server-side or no transfer state: fetch from API
    return this.http.get<T>(fullUrl).pipe(
      tap(response => {
        // Only set transfer state on server
        if (this.isServer) {
          this.transferState.set(STATE_KEY, response);
        }
      })
    );
  }

  // Method for POST requests with SSR support
  postData<T>(url: string, body: any, stateKey: string, options?: {
    headers?: HttpHeaders | {[header: string]: string | string[]},
    forceRefresh?: boolean,
  }): Observable<T> {
    // Create a unique key based on URL and request body
    const fullUrl = `${this.baseUrl}` + url;
    const uniqueKey = makeStateKey<T>(`${stateKey}_${this.createBodyHash(body)}`);

    // Client-side: Check if data is available in TransferState (skip if forceRefresh)
    if (!this.isServer && !options?.forceRefresh && this.transferState.hasKey(uniqueKey)) {
      const data = this.transferState.get(uniqueKey, null as T);
      this.transferState.remove(uniqueKey); // Clean up
      return of(data);
    }
    
    // Server-side or no transfer state: post to API
    return this.http.post<T>(fullUrl, body, options).pipe(
      tap(response => {
        // Only set transfer state on server
        if (this.isServer) {
          this.transferState.set(uniqueKey, response);
        }
      })
    );
  }

  // Helper method to create a simple hash of the request body
  private createBodyHash(body: any): string {
    if (!body) return 'empty';
    
    try {
      // Convert the body to a string and generate a simple hash
      const bodyStr = JSON.stringify(body);
      let hash = 0;
      for (let i = 0; i < bodyStr.length; i++) {
        const char = bodyStr.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32bit integer
      }
      return hash.toString();
    } catch (e) {
      // Fallback if body isn't serializable
      return Date.now().toString();
    }
  }
}